/**
 * WizardElement — F12.H T12.H.4.
 *
 * Kreator wieloetapowy — sekwencyjne kroki z przyciskami „Wstecz" /
 * „Dalej" / „Zakończ". Różni się od StepperElement wymuszoną sekwencją:
 * user nie może kliknąć Krok 3 jeśli nie skończył Krok 1 i 2.
 *
 * Walidacja per krok jest planowana w T12.H.10 (paleta + walidacja
 * połączona z polami). W obecnym shellu wszystkie kroki widzą wspólne
 * children.
 *
 * Settings: { steps: [{ id, label, description? }], showProgressBar? }
 */

import { useState } from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

import { Button } from '@/components/Button';
import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';

interface WizardStep {
  id: string;
  label: string;
  description?: string;
}

export const WizardElement: LayoutElementDefinition = {
  type: 'wizard',
  category: 'interaktywne',
  isContainer: true,
  label: 'Kreator',
  icon: 'Workflow',
  defaultSettings: () => ({
    steps: [
      { id: 'wz-1', label: 'Krok 1', description: 'Pierwszy krok kreatora' },
      { id: 'wz-2', label: 'Krok 2', description: 'Drugi krok kreatora' },
      { id: 'wz-3', label: 'Podsumowanie', description: 'Sprawdź i zakończ' },
    ] as WizardStep[],
    showProgressBar: true,
  }),
  render: ({ element, children, mode }) => {
    const settings = (element.settings ?? {}) as {
      steps?: WizardStep[];
      showProgressBar?: boolean;
    };
    const isBuilder = mode === 'builder';
    return (
      <WizardView
        steps={settings.steps ?? []}
        showProgressBar={settings.showProgressBar !== false}
        children={children}
        isBuilder={isBuilder}
        elementId={element.id}
      />
    );
  },
};

function WizardView({
  steps,
  showProgressBar,
  children,
  isBuilder,
  elementId,
}: {
  steps: WizardStep[];
  showProgressBar: boolean;
  children: React.ReactNode[];
  isBuilder: boolean;
  elementId: string;
}) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const currentStep = steps[currentIdx];
  const isFirst = currentIdx === 0;
  const isLast = currentIdx === steps.length - 1;

  return (
    <div
      className={`rounded-lg border border-border-primary p-4 ${isBuilder ? 'ring-2 ring-transparent hover:ring-accent-200 transition' : ''}`}
      data-testid={`wizard-element-${elementId}`}
      data-element-type="wizard"
    >
      {steps.length === 0 ? (
        isBuilder ? (
          <p className="text-xs italic text-text-muted">
            (brak kroków — dodaj w panelu właściwości)
          </p>
        ) : null
      ) : (
        <>
          {/* Progress bar */}
          {showProgressBar && (
            <div className="mb-4">
              <div className="mb-1 flex items-center justify-between text-xs text-text-secondary">
                <span>
                  Krok {currentIdx + 1} z {steps.length}
                </span>
                <span>{Math.round(((currentIdx + 1) / steps.length) * 100)}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-tertiary">
                <div
                  className="h-full bg-accent-500 transition-all"
                  style={{ width: `${((currentIdx + 1) / steps.length) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Step title + description */}
          {currentStep && (
            <div className="mb-3">
              <h3 className="text-base font-semibold text-text-primary">{currentStep.label}</h3>
              {currentStep.description && (
                <p className="mt-0.5 text-sm text-text-secondary">{currentStep.description}</p>
              )}
            </div>
          )}

          {/* Content */}
          <div className="mb-4 min-h-[80px]">
            {children.length > 0 ? (
              <div className="space-y-3">{children}</div>
            ) : isBuilder ? (
              <p className="text-xs italic text-text-muted">
                (zawartość kroku pusta — przeciągnij elementy)
              </p>
            ) : null}
          </div>

          {/* Navigation */}
          <div className="flex justify-between gap-2 border-t border-border-primary pt-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentIdx((p) => Math.max(0, p - 1))}
              disabled={isFirst}
              leftIcon={<ArrowLeft className="h-3.5 w-3.5" />}
            >
              Wstecz
            </Button>
            {isLast ? (
              <Button variant="primary" size="sm" leftIcon={<Check className="h-3.5 w-3.5" />}>
                Zakończ
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setCurrentIdx((p) => Math.min(steps.length - 1, p + 1))}
                rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
              >
                Dalej
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
